import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import ShotImage from './ShotImage';
import ShotMeta from './ShotMeta';

const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

// Modal photo viewer. Escape closes, arrows move, Tab is trapped, focus returns to the opener.
const Lightbox = ({ shots, index, failed, onFail, onClose, onIndex }) => {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const opener = useRef(document.activeElement);
  const total = shots.length;
  const shot = shots[index];

  useEffect(() => {
    const returnTo = opener.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (returnTo && typeof returnTo.focus === 'function') returnTo.focus();
    };
  }, []);

  const step = (delta) => onIndex((index + delta + total) % total);

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    } else if (event.key === 'Tab' && dialogRef.current) {
      const nodes = dialogRef.current.querySelectorAll(FOCUSABLE);
      if (nodes.length === 0) return;
      const firstNode = nodes[0];
      const lastNode = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === firstNode) {
        event.preventDefault();
        lastNode.focus();
      } else if (!event.shiftKey && document.activeElement === lastNode) {
        event.preventDefault();
        firstNode.focus();
      }
    }
  };

  return createPortal(
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the dialog owns its key handling
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      ref={dialogRef}
      onKeyDown={onKeyDown}
    >
      <div className="lightbox-backdrop" onClick={onClose} aria-hidden="true"></div>
      <div className="lightbox-panel">
        <div className="pane-strip">
          <span>
            photo {index + 1} / {total}
          </span>
          <button
            type="button"
            className="icon-btn"
            ref={closeRef}
            onClick={onClose}
            aria-label="Close photo viewer"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden="true" />
          </button>
        </div>
        <div className="lightbox-stage">
          <button
            type="button"
            className="icon-btn lightbox-step"
            onClick={() => step(-1)}
            aria-label="Previous photo"
          >
            <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
          </button>
          <ShotImage
            key={shot.src}
            shot={shot}
            failed={failed.has(shot.src)}
            onFail={onFail}
            className="lightbox-img"
          />
          <button
            type="button"
            className="icon-btn lightbox-step"
            onClick={() => step(1)}
            aria-label="Next photo"
          >
            <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
          </button>
        </div>
        <div className="lightbox-caption">
          <p className="shot-title">
            {shot.title || 'Drone photograph'}
            {shot.location ? <span className="shot-place">{shot.location}</span> : null}
          </p>
          <ShotMeta shot={shot} />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Lightbox;
